import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { Button, Drawer, ListItemButton } from '../../../../../../../ui';
import { addQuestionToAssessment } from '../../../../../../../utils/api_service/assessment_api';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import {
  Question,
  QuestionSearch,
  SubSkill,
} from '../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { Filter } from './Components';
import { useSelectQuestions } from './Hooks';

interface ISelectQuestions {
  isOpen: boolean;
  onCloseDrawer: () => void;
  onSave: (finalSelection: Question[]) => void;
  assessmentId: string;
  skillList: SelectOptions[] | undefined;
  levelList: SelectOptions[] | undefined;
  subSkillList: SubSkill[] | undefined;
  jobTitleList: SelectOptions[] | undefined;
  initialSelected: Question[];
  onAddQuestion: (q: Question) => void;
}

export const SelectQuestions: React.FC<ISelectQuestions> = ({
  isOpen,
  onCloseDrawer,
  onSave,
  assessmentId,
  skillList,
  levelList,
  subSkillList,
  jobTitleList,
  initialSelected,
  onAddQuestion,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SQ = ADMIN_PAGES_KEYS.jobOpenings.selectQuestions;
  const [searchObject, setSearchObject] = useState<QuestionSearch>({
    questionTitle: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    jobTitle: '',
    assessmentId,
  });

  const {
    questionsList,
    selectedQuestions,
    loading,
    totalCount,
    pageNumber,
    totalPages,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    goToNextPage,
    goToPrevPage,
    setIsSaving,
    isSaving,
  } = useSelectQuestions({ searchObject, initialSelected });

  const handleSaveClick = async () => {
    setIsSaving(true);

    // Find only the newly-selected ones:
    const toAdd = selectedQuestions.filter(
      (q) => !initialSelected.some((s) => s.id === q.id),
    );

    try {
      await Promise.all(
        toAdd.map((q) =>
          addQuestionToAssessment(
            /* your assessmentQuestionId */ '',
            q.id,
            assessmentId,
          ),
        ),
      );
      toast.success('Questions added successfully!');
      // Now notify parent of the final array and close
      onSave(selectedQuestions);
    } catch (err) {
      toast.error('Failed to add some questions.');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        width='w-full md:w-[800px]'
        title={t(SQ.drawerTitle)}
        footerContent={
          <div className='flex w-full flex-col justify-end gap-3 sm:flex-row sm:gap-5'>
            <Button
                label={t(SQ.cancel)}
                onClick={onCloseDrawer}
                variant='secondary'
                className='w-full sm:w-auto'
            />
            <Button
                label={isSaving ? t(SQ.saving) : t(SQ.save)}
                onClick={handleSaveClick}
                variant='primary'
                className='w-full sm:w-auto'
            />
          </div>
        }
    >
      <div className='flex h-full min-h-[70vh] flex-col gap-4 lg:h-[80vh] lg:flex-row lg:gap-5'>
        { /* Filter pane */ }
        <div className='w-full lg:w-[250px] lg:shrink-0'>
          <Filter
              skillList={skillList || []}
              levelList={levelList || []}
              subSkillList={subSkillList}
              jobTitleList={jobTitleList}
              searchObject={searchObject}
              setSearchObject={setSearchObject}
          />
        </div>

        { /* Divider */ }
        <div className='hidden border border-dashed border-gray-300 mx-2 lg:block' />

        { /* Main content: top and bottom halves */ }
        <div className='flex min-h-0 flex-1 flex-col'>
          { /* Top half: Available Questions */ }
          <div className='min-h-[260px] flex flex-col lg:h-1/2'>
            <div className='flex flex-wrap justify-between gap-2 items-center mb-2'>
              <h3 className='font-semibold text-base'>{ t(SQ.allQuestions) }</h3>
              <Button
                  variant='link'
                  label={t(SQ.selectAll)}
                  onClick={() => {
                    handleSelectAll();
                    questionsList.forEach(onAddQuestion);
                  }}
                  disabled={questionsList.length === 0}
              />
            </div>
            <div className='flex flex-wrap justify-between gap-2 items-center mb-2'>
              <small>
                { t(SQ.pageOf, { page: pageNumber, total: totalPages, count: totalCount }) }
              </small>
              <div className='flex gap-2'>
                <Button
                    label={t(SQ.previous)}
                    onClick={goToPrevPage}
                    variant='secondary'
                    disabled={pageNumber === 1 || loading}
                />
                <Button
                    label={t(SQ.next)}
                    onClick={goToNextPage}
                    variant='secondary'
                    disabled={pageNumber === totalPages || loading}
                />
              </div>
            </div>
            <div className='flex-1 overflow-y-auto border p-2 rounded'>
              { loading ? (
                <p className='text-sm text-gray-500'>{ t(SQ.loading) }</p>
              ) : questionsList.length === 0 ? (
                <p className='text-sm text-gray-500'>{ t(SQ.noQuestions) }</p>
              ) : (
                <ul className='space-y-2'>
                  { questionsList.map((q) => (
                    <ListItemButton
                        key={q.id}
                        onClick={() => {
                          handleSelect(q);
                          onAddQuestion(q);
                        }}
                    >
                      { q.question || t(SQ.untitled) }
                    </ListItemButton>
                  )) }
                </ul>
              ) }
            </div>
          </div>

          { /* Divider line */ }
          <div className='border-t border-gray-300 my-2' />

          { /* Bottom half: Selected Questions */ }
          <div className='min-h-[260px] flex flex-col lg:h-1/2'>
            <div className='flex flex-wrap justify-between gap-2 items-center mb-2'>
              <h3 className='font-semibold text-base'>{ t(SQ.selectedQuestions) }</h3>
              <Button
                  variant='link'
                  label={t(SQ.removeAll)}
                  onClick={handleRemoveAll}
                  disabled={selectedQuestions.length === 0}
              />
            </div>
            <ul className='flex-1 overflow-y-auto border p-2 rounded space-y-2'>
              { selectedQuestions.length === 0 ? (
                <li className='text-sm text-gray-500'>{ t(SQ.noSelected) }</li>
              ) : (
                selectedQuestions.map((q) => (
                  <ListItemButton
                      key={q.id}
                      isSelected
                      onClick={() => {}}
                      onRemove={() => handleRemove(q)}
                  >
                    { q.question || t(SQ.untitled) }
                  </ListItemButton>
                ))
              ) }
            </ul>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SelectQuestions;
